/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { PopupPlacementMixin } from '../../utils/popup-placement-mixin.js';
import tooltipStyles from './sh-tooltip.lit.scss.js';

/**
 * @prop {String} label - Default label. Defines the text that will be shown in the tooltip.
 * @prop {String} title - Default tooltip. Defines the title for the `long' variation of tooltip.
 * @prop {String} variation - Default long. Defines the type of tooltip. Two variations - short and long.
 * @prop {String} icon - Defines the icon.
 * @prop {Boolean} error - Default false. If set to `true`, the tooltip displays styles for error tooltip.
 * @prop {Boolean} visible - Default false. If set to `true`, the tooltip becomes visible. Is triggered automatically upon hovering the "target" element.
 * @prop {String} target - Defines the id of the tooltip for which the tooltip will be shown. Should be given as an id to the component for which a tooltip is required.
 * @prop {String} placement - Default top. Defines where the tooltip will be shown. Accepted values are - top, bottom, left, right, top-right, top-left, 'bottom-left, bottom-right, left-up, left-down, right-up, right-down.
 * @prop {Boolean} delay - If set to `true`, the tooltip is shown with a 400ms delay after hover.
 */
export class SHTooltip extends PopupPlacementMixin(LitElement) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      title: { type: String, reflect: true },
      variation: { type: String, reflect: true },
      target: { type: String, reflect: true },
      visible: { type: Boolean, reflect: true },
      placement: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      error: { type: Boolean, reflect: true },
      delay: { type: Boolean, reflect: true },
      _targetEl: { type: Object },
    };
  }
  static get styles() {
    return [super.styles, sharedStyles, tooltipStyles];
  }
  render() {
    return html`
      ${this.title && this.variation === 'long'
        ? html` <div class="tooltip-title">${this.title}</div> `
        : ''}
      <div class="label-wrapper">
        ${this.icon
          ? html` <sh-icon icon="${this.icon}" id="tooltipIcon" size="s"></sh-icon> `
          : ''}
        ${this.label ? html` <div class="tooltip-label">${this.label}</div> ` : ''}
      </div>
      <slot></slot>
    `;
  }
  constructor() {
    super();
    this.variation = 'long';
    this.placement = 'top';
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('visible')) {
      this._handleVisible();
    }
    if (changedProperties.has('target')) {
      this._handleTarget();
    }
    if (changedProperties.has('error')) {
      this._handleError();
    }
    if (changedProperties.has('placement')) {
      if (this.placement && this.classList.contains('is-visible')) {
        this.show();
      }
    }
    changedProperties.forEach((oldValue, propName) => {
      if (propName === '_targetEl' && oldValue) {
        this._removeTargetEventListener(oldValue);
      }
    });
    const listOfProperties = [
      'label',
      'title',
      'variation',
      'target',
      'visible',
      'placement',
      'icon',
      'error',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  firstUpdated() {
    super.firstUpdated();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    this._showListener = this._setVisible.bind(this);
    this._hideListener = this._clearVisible.bind(this);
    this._tooltipMouseEnter = this._tooltipMouseEnterActions.bind(this);
    this._tooltipMouseLeave = this._tooltipMouseLeaveActions.bind(this);
    this.addEventListener('mouseenter', this._tooltipMouseEnter);
    this.addEventListener('mouseleave', this._tooltipMouseLeave);
  }
  _tooltipMouseEnterActions() {
    this.visible = true;
  }
  _tooltipMouseLeaveActions() {
    this.visible = false;
  }
  _handleTarget() {
    this._targetEl = this.getTarget(this.target);
    if (this._targetEl) {
      this._targetEl.addEventListener('mouseenter', this._showListener);
      this._targetEl.addEventListener('mouseleave', this._hideListener);
      this._targetEl.addEventListener('focus', this._showListener);
      this._targetEl.addEventListener('blur', this._hideListener);
      this._targetEl.addEventListener('tap', this._hideListener);
      if (this.visible) {
        this.show();
      }
    }
  }
  _removeTargetEventListener(oldTarget) {
    oldTarget.removeEventListener('mouseenter', this._showListener);
    oldTarget.removeEventListener('focus', this._showListener);
    oldTarget.removeEventListener('mouseleave', this._hideListener);
    oldTarget.removeEventListener('blur', this._hideListener);
    oldTarget.removeEventListener('tap', this._hideListener);
  }
  disconnectedCallback() {
    if (this._targetEl) {
      this._removeTargetEventListener(this._targetEl);
    }
    this.removeEventListener('mouseenter', this._tooltipMouseEnter);
    this.removeEventListener('mouseleave', this._tooltipMouseLeave);
    super.disconnectedCallback();
  }
  _handleVisible() {
    if (this.visible) {
      this.show();
    }
    if (!this.visible) {
      this.hide();
    }
  }
  _setVisible() {
    if (!this.classList.contains('touch-device')) {
      this.visible = true;
    }
  }
  _clearVisible() {
    this.visible = false;
  }
  _handleError() {
    if (this.error) {
      this.icon = 'error';
    }
  }
}

customElements.define('sh-tooltip', SHTooltip);
